'use client'

import Link from 'next/link'
import Image from 'next/image'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  FiArrowRight, 
  FiPhone, 
  FiMessageCircle, 
  FiCalendar,
  FiCheck,
  FiStar,
  FiHeart,
  FiShield,
  FiClock,
  FiAward,
  FiUsers,
  FiBookOpen,
  FiActivity,
  FiSmile,
  FiMic,
  FiVideo,
  FiFileText,
  FiClipboard,
  FiCreditCard,
  FiCheckCircle,
  FiArrowDown,
  FiPlay,
  FiZap
} from 'react-icons/fi'
import { siteConfig } from '@/config/site'
import { NewsletterForm } from '@/components/NewsletterForm'
import { TestimonialCard } from '@/components/TestimonialCard'
import type { Testimonial } from '@/lib/types'

const featuredTestimonials: Testimonial[] = [
  {
    id: '1',
    patientName: 'Nyakallo Mothupi',
    patientTitle: 'Patient',
    content: 'I received an excellent medical attention. Doctor N Matla offers guidance regarding mental health and physical wellness before prescribing any drugs.',
    rating: 5,
    date: '2024-01-15',
    verified: true,
  },
  {
    id: '2',
    patientName: 'Counselling client',
    patientTitle: 'Patient',
    content: 'One day, when life was hitting especially hard, I made the decision to seek professional help because I could no longer cope on my own. During that time, I was struggling with both personal and work-related challenges that felt overwhelming and difficult to navigate. I then had the opportunity to attend a counselling session with Dr. Nthabiseng Matla during this particularly difficult period in my life. The session was arranged by a good friend who happened to be one of her loyal clients. From the very beginning, Dr. Matla created a safe, supportive, and non-judgmental space where I felt truly heard and understood, knowing that personal issues can be sensitive sometimes. Her approach was both compassionate and insightful. She helped me unpack complex emotions that I had been struggling to process on my own. One of the most impactful parts of the session was how she guided me through confronting and understanding my childhood trauma. For the first time, I was able to clearly see how my past experiences were influencing how I feel and respond to situations in my adult life. That level of clarity was incredibly empowering. Through her guidance, I was able to regain my strength and rebuild my sense of self. She helped me reshape my goals in a way that felt both realistic and meaningful, and I left the session with a renewed sense of direction and purpose. I now feel more grounded, self-aware, and confident in who I want Neo to become. I am truly grateful for the journey we walked with her during our so many subsequent sessions after that one. Anyone seeking professional, empathetic, and transformative counselling support - Dr. Nthabiseng Matla will be your go to person.',
    rating: 5,
    date: '2024-01-10',
    verified: true,
  },
  {
    id: '3',
    patientName: 'James',
    patientTitle: 'Patient',
    content: 'Best doctor I could ask for. Always listens and provides clear explanations about my health.',
    rating: 5,
    date: '2024-01-05',
    verified: true,
  },
]

const services = [
  { 
    icon: FiHeart, 
    title: 'General Practice', 
    desc: 'Comprehensive health assessments and routine check-ups',
    color: 'from-rose-500 to-rose-600'
  },
  { 
    icon: FiShield, 
    title: 'Vaccinations', 
    desc: 'Preventive care programs and immunizations',
    color: 'from-teal-500 to-teal-600'
  },
  { 
    icon: FiActivity, 
    title: 'Lab Services', 
    desc: 'Diagnostic testing and health screenings',
    color: 'from-violet-500 to-violet-600'
  },
  { 
    icon: FiZap, 
    title: 'Mental Wellness', 
    desc: 'Psychological support and stress management',
    color: 'from-amber-500 to-amber-600'
  },
  { 
    icon: FiSmile, 
    title: 'Life Coaching', 
    desc: 'Personal development and goal achievement',
    color: 'from-emerald-500 to-emerald-600'
  },
  { 
    icon: FiMic, 
    title: 'Radio Programs', 
    desc: 'Health education through media',
    color: 'from-sky-500 to-sky-600'
  },
]

const stats = [
  { value: '10+', label: 'Years Experience', icon: FiAward },
  { value: '5000+', label: 'Patients Served', icon: FiUsers },
  { value: '98%', label: 'Satisfaction Rate', icon: FiStar },
]

const floatingShapes = [
  { size: 300, x: '5%', y: '10%', delay: 0, duration: 10, gradient: 'from-teal-400/20 to-teal-600/10' },
  { size: 200, x: '80%', y: '20%', delay: 1, duration: 8, gradient: 'from-rose-400/20 to-rose-600/10' },
  { size: 150, x: '70%', y: '60%', delay: 2, duration: 9, gradient: 'from-violet-400/20 to-violet-600/10' },
  { size: 250, x: '10%', y: '60%', delay: 1.5, duration: 8.5, gradient: 'from-amber-400/20 to-amber-600/10' },
]

const credentials = [
  'Bachelor of Medicine and Surgery (MBChB) - University of Pretoria',
  'Certified Life Coach',
  'Radio Programs Doctor',
]

export default function Home() {
  return (
    <>
      <style>{`
        @keyframes homeGrad {
          0%,100% { background-position: 0 50%; }
          50%      { background-position: 100% 50%; }
        }
        @keyframes homeShimmer {
          0%   { background-position: -200% center; }
          100% { background-position:  200% center; }
        }
        @keyframes homeCTAGrad {
          0%,100% { background-position: 0 50%; }
          50%      { background-position: 100% 50%; }
        }
        .home-hero-bg {
          background: linear-gradient(135deg,#0c1a2e 0%,#0F4C3A 28%,#0F766E 55%,#0891b2 80%,#1e3a5f 100%);
          background-size: 400% 400%;
          animation: homeGrad 12s ease infinite;
        }
        .shimmer-home {
          background: linear-gradient(90deg,#5eead4 0%,#67e8f9 45%,#5eead4 90%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: homeShimmer 3s linear infinite;
        }
        .home-svc-card {
          transition: box-shadow 0.35s ease, transform 0.35s ease;
        }
        .home-svc-card:hover {
          box-shadow: 0 20px 60px rgba(15,118,110,0.13);
          transform: perspective(900px) translateY(-6px) rotateX(1.5deg);
        }
        .home-cta-bg {
          background: linear-gradient(135deg,#0F766E 0%,#0D9488 38%,#0891b2 72%,#1e3a5f 100%);
          background-size: 400% 400%;
          animation: homeCTAGrad 10s ease infinite;
        }
      `}</style>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden home-hero-bg">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingShapes.map((shape, i) => (
            <motion.div
              key={i}
              className={`absolute rounded-full blur-3xl bg-gradient-to-br ${shape.gradient}`}
              style={{ width: shape.size, height: shape.size, left: shape.x, top: shape.y }}
              animate={{
                y: [0, -40, 0],
                x: [0, 20, 0],
                scale: [1, 1.15, 1],
              }}
              transition={{
                duration: shape.duration,
                delay: shape.delay,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          ))}
          
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
            backgroundSize: '60px 60px',
          }} />
        </div>


        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 md:py-20 pb-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="text-center lg:text-left"
            >
              {/* Mobile Hero Image */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9, rotate: -2 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="lg:hidden relative rounded-3xl overflow-hidden shadow-2xl mb-8 max-w-xs mx-auto"
              >
                <div className="aspect-[4/5] bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
                  <Image
                    src="/images/PHOTO-2024-05-28-22-23-49.jpg"
                    alt="Dr. Nthabiseng Matla"
                    width={400}
                    height={500}
                    className="w-full h-full object-cover"
                    priority
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500/20 border border-teal-500/30 text-teal-300 text-sm font-medium mb-6"
              >
                <FiStar className="w-4 h-4" />
                <span>Trusted by thousands of patients</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
              >
                Integrated Healthcare for Your{' '}
                <span className="shimmer-home">
                  Body & Mind
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="text-base sm:text-xl text-slate-300 mb-8 max-w-xl mx-auto lg:mx-0"
              >
                Personalized medical care, mental wellness support and life coaching delivered with compassion, clinical excellence and a patient-centered approach.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              >
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <a
                    href={`https://wa.me/${siteConfig.whatsapp.replace(/[^\d+]/g, '')}`}
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl font-semibold text-lg shadow-xl shadow-teal-500/30 hover:shadow-2xl hover:shadow-teal-500/40 transition-all duration-300"
                  >
                    <FiMessageCircle className="w-5 h-5" />
                    <span>Book Consultation</span>
                    <FiArrowRight className="w-5 h-5" />
                  </a>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Link
                    href="/about"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-xl font-semibold text-lg hover:bg-white/20 transition-all duration-300"
                  >
                    Learn More
                  </Link>
                </motion.div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.6 }}
                className="mt-12 pt-8 border-t border-white/10"
              >
                <p className="text-slate-400 text-sm mb-4">Trusted by patients across South Africa and Lesotho.</p>
                <div className="flex flex-wrap gap-6 justify-center lg:justify-start">
                  {stats.map((stat, i) => (
                    <motion.div 
                      key={i} 
                      className="flex items-center gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.8 + i * 0.1 }}
                    >
                      <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center">
                        <stat.icon className="w-5 h-5 text-teal-400" />
                      </div>
                      <div>
                        <p className="text-lg sm:text-2xl font-bold text-white">{stat.value}</p>
                        <p className="text-slate-400 text-[11px]">{stat.label}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </motion.div>

            {/* Visual Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ delay: 0.3, duration: 0.8, ease: 'easeOut' }}
              className="relative hidden lg:block"
            >
              <motion.div
                className="relative z-10"
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <div className="aspect-[4/5] bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
                    <Image
                      src="/images/PHOTO-2024-05-28-22-23-49.jpg"
                      alt="Dr. Nthabiseng Matla"
                      width={400}
                      height={500}
                      className="w-full h-full object-cover"
                      priority
                    />
                  </div>
                  
                  <motion.div
                    className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1, duration: 0.5 }}
                  >
                    <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                      <FiCheckCircle className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-800">Verified</p>
                      <p className="text-sm text-slate-500">Medical License</p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>

              <div className="absolute top-10 right-10 w-72 h-72 bg-teal-500/20 rounded-full blur-3xl -z-10" />
              <div className="absolute bottom-10 left-10 w-48 h-48 bg-teal-400/20 rounded-full blur-2xl -z-10" />
            </motion.div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0 pointer-events-none z-10">
          <svg viewBox="0 0 1440 56" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 56 C360 0 1080 0 1440 56 L1440 56 L0 56Z" fill="#f8fafc"/>
          </svg>
        </div>
        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-14 left-1/2 -translate-x-1/2 z-20"
          animate={{ y: [0, 12, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-8 h-14 rounded-full border-2 border-white/20 flex items-start justify-center p-2">
            <motion.div
              className="w-1.5 h-3 bg-teal-400 rounded-full"
              animate={{ y: [0, 16, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-100 text-teal-700 text-sm font-medium mb-4">
              <FiHeart className="w-4 h-4" />
              Our Services
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              Comprehensive Healthcare Solutions
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Tailored to your unique needs, from routine check-ups to specialized care
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {services.map((service, i) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                whileHover={{ y: -8 }}
                className="group"
              >
                <div className="home-svc-card bg-white rounded-2xl p-8 shadow-lg shadow-slate-200/50 border border-slate-100 h-full">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <service.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                  <p className="text-slate-600 mb-6">{service.desc}</p>
                  <Link 
                    href="/services" 
                    className="inline-flex items-center gap-2 text-teal-600 font-medium hover:gap-3 transition-all"
                  >
                    Learn more <FiArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="text-center mt-12"
          >
            <Link href="/services" className="inline-flex items-center gap-2 px-8 py-4 bg-slate-900 text-white rounded-xl font-semibold hover:bg-slate-800 transition-colors">
              View All Services <FiArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <Image
                  src="/images/logo1.jpeg"
                  alt="About Dr. Nthabiseng Matla"
                  width={500}
                  height={600}
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-teal-500 rounded-2xl -z-10" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-rose-100 text-rose-700 text-sm font-medium mb-4">
                <FiHeart className="w-4 h-4" />
                About the Doctor
              </span>
              <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
                Dr. Nthabiseng Matla
              </h2>
              <p className="text-xl font-semibold text-teal-600 mb-6">
                Medical Doctor | Certified Life Coach | Transformational Speaker
              </p>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Dr. Nthabiseng Matla is a compassionate medical doctor, certified life coach, transformational speaker, and radio doctor devoted to delivering integrated, patient-centered healthcare.
              </p>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                With over a decade of medical experience, she has built a reputation for providing personalized, holistic care grounded in empathy, clinical excellence and a deep commitment to human dignity. Her work uniquely bridges medicine and personal development, supporting individuals not only in healing physical conditions but also in achieving emotional and mental well-being.
              </p>

              <div className="space-y-4 mb-8">
                {credentials.map((cred, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <FiCheck className="w-4 h-4 text-teal-600" />
                    </div>
                    <span className="text-slate-700 font-medium">{cred}</span>
                  </motion.div>
                ))}
              </div>

              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Link
                  href="/about"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl font-semibold shadow-lg shadow-teal-500/30"
                >
                  Learn More About Dr. Matla <FiArrowRight className="w-5 h-5" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-950 relative overflow-hidden">
        <div className="absolute inset-0">
          <Image src="/images/PHOTO-2024-05-28-21-34-59.jpg" alt="" fill className="object-cover opacity-10" sizes="100vw"/>
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950 to-slate-900/95"/>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-3 gap-8">
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/10 flex items-center justify-center">
                  <stat.icon className="w-8 h-8 text-teal-400" />
                </div>
                <p className="text-4xl sm:text-5xl font-bold text-white mb-2">{stat.value}</p>
                <p className="text-slate-400">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-100 text-amber-700 text-sm font-medium mb-4">
              <FiStar className="w-4 h-4" />
              Testimonials
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Real experiences from our valued clients
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {featuredTestimonials.map((testimonial, i) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
              >
                <TestimonialCard testimonial={testimonial} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 home-cta-bg relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Ready to Start Your Health Journey?
            </h2>
            <p className="text-xl text-teal-100 mb-10 max-w-2xl mx-auto">
              Schedule a consultation today and experience healthcare that puts you first.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <a
                  href={`https://wa.me/${siteConfig.whatsapp.replace(/[^\d+]/g, '')}`}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-teal-700 rounded-xl font-semibold text-lg shadow-xl"
                >
                  <FiMessageCircle className="w-5 h-5" />
                  <span>Book via WhatsApp</span>
                </a>
              </motion.div>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <a
                  href={`tel:${siteConfig.phone}`}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-teal-700/50 backdrop-blur-sm border border-teal-500 text-white rounded-xl font-semibold text-lg"
                >
                  <FiPhone className="w-5 h-5" />
                  <span>Call Now</span>
                </a>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Stay Informed</h2>
            <p className="text-slate-600 mb-8">
              Subscribe to receive health tips, practice updates, and wellness tips directly to your inbox.
            </p>
            <NewsletterForm />
          </motion.div>
        </div>
      </section>
    </>
  )
}
